import * as React from "react";
import defaultProps from "baseui/select/default-props";
import { ChevronDown } from "baseui/icon";
import { expandPadding } from "../utils/styles";
import { getInputOverrides } from "../Input";
import { mergeOverrides } from "baseui/helpers/overrides";
import { Select as BaseSelect, SelectProps, SIZE } from "baseui/select";
import { useSelect } from "./useSelect";
import { useStyletron } from "kanopi-components/theme/useStyletron";

export type TSelectProps = {
  onChange?: (...args: unknown[]) => void;
  valueKey?: string;
  multi?: boolean;
  value?: any;
  full?: boolean;
  positive?: boolean;
  required?: boolean;
  // Should this forward controlRef instead?
  inputRef?: SelectProps["controlRef"];
} & SelectProps;

/**
 * A light-wrapper around BaseWeb's Select component.
 *
 * This aims to make managing values a little easier. Most basic
 * usages only care about the "value" of the selected option, which is
 * the default operation. By setting the "full" flag the full option
 * is returned instead of just the value. This tends to be useful for
 * autocomplete applications.
 */
export function Select(props: TSelectProps) {
  const {
    onChange,
    valueKey = defaultProps.valueKey,
    multi = defaultProps.multi,
    searchable = false,
    value,
    full,
    positive,
    overrides: selectOverrides,
    inputRef,
    size,
    clearable,
    defaultValue,
    ...restProps
  } = props;

  const [_value, handleChange] = useSelect({ value, multi, full, valueKey, onChange });
  const [, theme] = useStyletron();

  React.useEffect(() => {
    if (defaultValue != null) {
      const option = props.options.find((option) => {
        return option.value === defaultValue;
      });
      if (option) {
        handleChange({
          option,
          type: "select",
          value: [option],
        });
      }
    }
  }, []);

  // Without the `_value || undefined` below, we end up with a little
  // space between the placeholder and the left edge.
  return (
    <BaseSelect
      onChange={handleChange}
      valueKey={valueKey}
      value={_value || undefined}
      overrides={mergeOverrides(
        {
          ...getSelectOverrides(props),
          SelectArrow: {
            props: {
              size: 24,
              overrides: {
                Svg: {
                  component: ChevronDown,
                },
              },
            },
          },
          Popover: {
            props: {
              overrides: {
                Body: {
                  style: {
                    zIndex: theme.zIndex.popover,
                  },
                },
              },
            },
          },
        },
        selectOverrides
      )}
      positive={positive}
      controlRef={inputRef}
      size={size}
      clearable={clearable}
      searchable={searchable}
      multi={multi}
      {...restProps}
    />
  );
}

function getSelectOverrides(props) {
  const { size } = props;
  const overrides = getInputOverrides(props);
  const isMini = size === SIZE.mini;

  // Note that Root, ValueContainer, and InputContainer all depend on
  // the currently selected font size, hence them being overridden
  // here.
  //
  // TODO: Perhaps it would be better to change the definition of our
  // fonts in our desing system to better match the way they're used
  // in BaseWeb?
  return {
    Root: {
      style: ({ $theme }) => ({
        ...(isMini ? $theme.typography.font550 : {}),
      }),
    },
    ControlContainer: overrides.Root,
    // Needs a better system
    ValueContainer: {
      style: ({ $theme }) => ({
        marginLeft: 0,
        ...expandPadding(0),
        fontSize: isMini ? $theme.typography.font550.fontSize : undefined,
      }),
    },
    Input: overrides.Input,
    InputContainer: {
      style: ({ $theme }) => ({
        minWidth: "2px",
        maxHeight: isMini ? $theme.typography.font550.lineHeight : undefined,
      }),
    },
    IconsContainer: {
      style: {
        marginBottom: isMini ? "0" : "10px",
        paddingRight: 0,
      },
    },
  };
}

Select.SIZE = SIZE;
